import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * 계층형 트리 메뉴 컴포넌트
 * 원본 menu-tree.html과 동일한 구조
 */
const MenuTree = ({ items, level = 0 }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const treeRef = useRef(null);

  // href를 React Router 경로로 변환하는 헬퍼 함수
  const convertToReactRoute = (href) => {
    if (!href || href === '#') return null;

    // 외부 URL인 경우 (http://, https://, //)
    if (/^(https?:)?\/\//.test(href)) {
      return null; // 외부 링크는 그대로 사용
    }

    // React Router 경로로 변환
    return href;
  };

  // 원본 JavaScript 초기화 로직 실행
  useEffect(() => {
    if (treeRef.current) {
      // 원본 menu-tree.html의 JavaScript 초기화 로직 실행
      const initNavTree = () => {
        const rootTrees = treeRef.current.querySelectorAll('.nav-tree-root');
        if (!rootTrees || rootTrees.length === 0) {
          return;
        }

        rootTrees.forEach((root) => {
          // 루트는 항상 펼쳐진 상태
          root.classList.add('is-expanded');

          function toggleItem(item) {
            if (!item || !item.classList.contains('has-children')) {
              return;
            }

            const childList = item.querySelector(':scope > .nav-tree');
            if (!childList) {
              return;
            }

            const isExpanded = childList.classList.contains('is-expanded');
            childList.classList.toggle('is-expanded', !isExpanded);
            childList.classList.toggle('is-collapsed', isExpanded);
            item.classList.toggle('is-open', !isExpanded);

            const toggleBtn = item.querySelector(':scope > .nav-tree__link-wrapper > .nav-tree__toggle');
            if (toggleBtn && toggleBtn.tagName === 'BUTTON') {
              toggleBtn.setAttribute('aria-expanded', (!isExpanded).toString());
              const icon = toggleBtn.querySelector('.nav-tree__toggle-icon');
              if (icon) {
                icon.textContent = !isExpanded ? '−' : '+';
              }
            }
          }

          root.querySelectorAll('.nav-tree__item.has-children').forEach((item) => {
            const toggleBtn = item.querySelector(':scope > .nav-tree__link-wrapper > button.nav-tree__toggle');
            if (toggleBtn) {
              toggleBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleItem(item);
              }, { passive: false });
            }

            const link = item.querySelector(':scope > .nav-tree__link-wrapper > .nav-tree__link');
            if (link) {
              link.addEventListener('click', (e) => {
                // 원본과 동일하게 모든 레벨에서 링크 클릭 시 토글
                e.preventDefault();
                toggleItem(item);
              });
            }
          });

          function normalizePath(path) {
            if (!path) {
              return '/';
            }
            try {
              const decoded = decodeURIComponent(path);
              return decoded.replace(/\/+$/, '') || '/';
            } catch (e) {
              return path.replace(/\/+$/, '') || '/';
            }
          }

          function highlightActiveLink() {
            const currentPath = normalizePath(window.location.pathname);
            let activeLink = null;

            root.querySelectorAll('.nav-tree__link').forEach((link) => {
              const href = link.getAttribute('href');
              if (!href || href === '#') {
                return;
              }
              try {
                const linkUrl = new URL(href, window.location.origin);
                const linkPath = normalizePath(linkUrl.pathname);
                if (linkPath === currentPath) {
                  activeLink = link;
                }
              } catch (e) {
                // ignore invalid URLs
              }
            });

            if (!activeLink) {
              return;
            }

            activeLink.classList.add('is-active');
            if (!activeLink.hasAttribute('tabindex')) {
              activeLink.setAttribute('tabindex', '-1');
            }
            try {
              activeLink.focus({ preventScroll: true });
            } catch (e) {
              activeLink.focus();
            }

            let parentItem = activeLink.closest('.nav-tree__item');
            while (parentItem) {
              parentItem.classList.add('is-open');
              const childList = parentItem.querySelector(':scope > .nav-tree');
              if (childList) {
                childList.classList.add('is-expanded');
                childList.classList.remove('is-collapsed');
              }

              const parentToggle = parentItem.querySelector(':scope > .nav-tree__link-wrapper > .nav-tree__toggle');
              if (parentToggle && parentToggle.tagName === 'BUTTON') {
                parentToggle.setAttribute('aria-expanded', 'true');
                const toggleIcon = parentToggle.querySelector('.nav-tree__toggle-icon');
                if (toggleIcon) {
                  toggleIcon.textContent = '−';
                }
              }

              parentItem = parentItem.parentElement ? parentItem.parentElement.closest('.nav-tree__item') : null;
            }

            // 트리뷰 스크롤 컨테이너 내에서 활성 항목으로 스크롤 이동
            function scrollToActiveLink() {
              let scrollContainer = null;
              let element = activeLink;

              while (element && element !== document.body) {
                const computedStyle = window.getComputedStyle(element);
                const overflowY = computedStyle.overflowY;
                const overflow = computedStyle.overflow;

                if ((overflowY === 'auto' || overflowY === 'scroll' || overflow === 'auto' || overflow === 'scroll') &&
                    (element.scrollHeight > element.clientHeight)) {
                  scrollContainer = element;
                  break;
                }

                if (element.classList && (
                    element.classList.contains('nav-layout__menu') ||
                    element.classList.contains('sidebar-menu') ||
                    element.classList.contains('tree-menu-container') ||
                    element.classList.contains('nav-layout__inner')
                )) {
                  scrollContainer = element;
                  break;
                }

                element = element.parentElement;
              }

              if (scrollContainer) {
                const containerRect = scrollContainer.getBoundingClientRect();
                const linkRect = activeLink.getBoundingClientRect();

                const linkTopRelative = linkRect.top - containerRect.top + scrollContainer.scrollTop;
                const linkBottomRelative = linkTopRelative + activeLink.offsetHeight;

                const scrollTop = scrollContainer.scrollTop;
                const scrollBottom = scrollTop + scrollContainer.clientHeight;

                let targetScrollTop = scrollTop;
                if (linkTopRelative < scrollTop) {
                  targetScrollTop = linkTopRelative - 20;
                } else if (linkBottomRelative > scrollBottom) {
                  targetScrollTop = linkBottomRelative - scrollContainer.clientHeight + 20;
                }

                if (targetScrollTop !== scrollTop) {
                  scrollContainer.scrollTo({
                    top: targetScrollTop,
                    behavior: 'smooth'
                  });
                }
              } else {
                try {
                  activeLink.scrollIntoView({ block: 'center', behavior: 'smooth' });
                } catch (e) {
                  activeLink.scrollIntoView();
                }
              }
            }

            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                scrollToActiveLink();
              });
            });
          }

          highlightActiveLink();
        });
      };

      // DOM이 렌더링된 후 초기화
      const timer = setTimeout(() => {
        initNavTree();
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [items, location.pathname, level]);

  if (!items || items.length === 0) {
    return null;
  }

  // React 상태 관리는 제거하고 원본 JavaScript 로직만 사용
  // toggleItem은 원본 JavaScript에서 처리

  const renderMenuItem = (item, index, parentPath = [], currentLevel = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const href = item.href && item.href !== '#' ? item.href : '#';
    const route = convertToReactRoute(href);
    const isActive = location.pathname === (route || href);

    const handleLinkClick = (e) => {
      // 원본 JavaScript 이벤트 리스너가 실행되지 않도록 먼저 처리
      e.preventDefault();
      e.stopPropagation();
      if (e.nativeEvent) {
        e.nativeEvent.stopImmediatePropagation();
      }

      if (hasChildren) {
        // 자식이 있으면 토글만 수행 (원본 JavaScript에서 처리)
        // preventDefault는 이미 위에서 처리됨
      } else if (route) {
        // React Router 경로인 경우
        navigate(route);
      } else if (href !== '#') {
        // 외부 링크인 경우
        window.location.href = href;
      }
      // href가 '#'인 경우는 아무것도 하지 않음
    };

    return (
      <li
        key={index}
        className={`nav-tree__item ${hasChildren ? 'has-children' : ''}`}
      >
        <div className={`nav-tree__link-wrapper ${hasChildren ? 'has-toggle' : ''}`}>
          {hasChildren ? (
            currentLevel === 0 ? (
              <span className="nav-tree__toggle nav-tree__toggle--placeholder" aria-hidden="true"></span>
            ) : (
              <button
                type="button"
                className="nav-tree__toggle"
                aria-label="Toggle submenu"
                aria-expanded="false"
              >
                <span className="nav-tree__toggle-icon">+</span>
              </button>
            )
          ) : (
            <span className="nav-tree__toggle nav-tree__toggle--placeholder" aria-hidden="true"></span>
          )}
          <a
            href={route || (href !== '#' ? href : '#')}
            className={`nav-tree__link ${isActive ? 'is-active' : ''}`}
            data-has-children={hasChildren}
            onClick={handleLinkClick}
          >
            {item.label}
          </a>
        </div>
        {hasChildren && (
          <ul className="nav-tree nav-tree-children is-collapsed">
            {item.children.map((child, childIndex) => renderMenuItem(child, childIndex, [...parentPath, index], currentLevel + 1))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div ref={treeRef}>
      <ul className={level === 0 ? 'nav-tree nav-tree-root' : 'nav-tree nav-tree-children is-collapsed'}>
        {items.map((item, index) => renderMenuItem(item, index, [], level))}
      </ul>
    </div>
  );
};

export default MenuTree;

